import type { EnemyDef } from '../core/combat';
import type { Rng } from '../core/intent';
import type { NodeType } from './map';

export type ComponentId =
  | 'ember_fragment'
  | 'frost_crystal'
  | 'venom_component'
  | 'volt_component'
  | 'masonry_fragment'
  | 'prism_dust'
  | 'impact_core';

export interface ComponentDrop {
  readonly id: ComponentId;
  readonly name: string;
  readonly count: number;
}

export interface ComponentInfo {
  readonly name: string;
  readonly plural: string;
  readonly abbr: string;
}

export const COMPONENTS: Record<ComponentId, ComponentInfo> = {
  ember_fragment: { name: 'Ember Fragment', plural: 'Ember Fragments', abbr: 'EF' },
  frost_crystal: { name: 'Frost Crystal', plural: 'Frost Crystals', abbr: 'FC' },
  venom_component: { name: 'Venom Component', plural: 'Venom Components', abbr: 'VC' },
  volt_component: { name: 'Volt Component', plural: 'Volt Components', abbr: 'VT' },
  masonry_fragment: { name: 'Masonry Fragment', plural: 'Masonry Fragments', abbr: 'MF' },
  prism_dust: { name: 'Prism Dust', plural: 'Prism Dust', abbr: 'PD' },
  impact_core: { name: 'Impact Core', plural: 'Impact Cores', abbr: 'IC' },
};

interface DropRange {
  readonly id: ComponentId;
  readonly min: number;
  readonly max: number;
}

const ENEMY_DROPS: Record<string, DropRange> = {
  shardling: { id: 'ember_fragment', min: 1, max: 2 },
  junk_golem: { id: 'masonry_fragment', min: 1, max: 2 },
  prism_wisp: { id: 'prism_dust', min: 1, max: 2 },
  crystal_sage: { id: 'frost_crystal', min: 1, max: 2 },
  the_mason: { id: 'impact_core', min: 2, max: 3 },
};

const BONUS_POOL: readonly ComponentId[] = [
  'ember_fragment',
  'frost_crystal',
  'venom_component',
  'volt_component',
  'masonry_fragment',
  'prism_dust',
  'impact_core',
];

function rollCount(rng: Rng, min: number, max: number): number {
  return min + Math.floor(rng() * (max - min + 1));
}

/**
 * Component drops for the enemies of a finished fight.
 *
 * Every enemy identity maps to one component; elite and boss nodes add a
 * bonus roll on top, and a boss is guaranteed an impact core. Same-id drops
 * aggregate so the reward screen reads "3× Ember Fragments" rather than
 * listing each enemy separately.
 */
export function componentsForEnemies(
  enemies: readonly EnemyDef[],
  rng: Rng,
  nodeType?: NodeType,
): ComponentDrop[] {
  const totals = new Map<ComponentId, number>();
  const add = (id: ComponentId, count: number): void => {
    if (count <= 0) return;
    totals.set(id, (totals.get(id) ?? 0) + count);
  };

  for (const enemy of enemies) {
    const def = ENEMY_DROPS[enemy.id];
    if (!def) continue;
    add(def.id, rollCount(rng, def.min, def.max));
  }

  if (nodeType === 'elite' || nodeType === 'boss') {
    const bonusId = BONUS_POOL[Math.floor(rng() * BONUS_POOL.length)];
    if (bonusId) add(bonusId, rollCount(rng, 1, 2));
  }
  if (nodeType === 'boss' && !totals.has('impact_core')) {
    add('impact_core', rollCount(rng, 2, 3));
  }

  const out: ComponentDrop[] = [];
  for (const [id, count] of totals) {
    out.push({ id, name: COMPONENTS[id].name, count });
  }
  return out;
}
